import type { Event, Map as MapboxMap } from 'mapbox-gl';

/**
 * Registers a map event listener with automatic cleanup capability.
 *
 * This utility function provides a clean way to register event listeners on Mapbox GL JS maps
 * while ensuring proper cleanup to prevent memory leaks. It returns a cleanup function that
 * can be called to remove the event listener, it follows map.on type signature.
 *
 * @param map - The Mapbox GL JS map instance to register the event on
 * @param type - The event type to listen for (e.g., 'click', 'mouseenter', 'mousemove')
 * @param layerIdOrHandler - Either a layer ID or the handler directly
 * @param maybeHandler - Optional handler if layer ID is provided
 * @param args - Additional arguments to pass to the handler function
 * @returns A cleanup function that removes the event listener when called
 *
 * @example
 * ```typescript
 * useEffect(() => {
 *    const cleanupHover = registerMapEvent(
 *          map.current,
 *          'mouseenter',
 *          'traffic-layer',
 *           handleHover,
 *           projectId
 *        );
 *    return () => {
 *        cleanupHover();
 *    }
 * })
 * ```
 */
export function registerMapEvent<T extends Event, TArgs extends unknown[] = []>(
  map: MapboxMap,
  type: string,
  layerIdOrHandler: string | ((e: T, ...args: TArgs) => void),
  maybeHandler?: (e: T, ...args: TArgs) => void,
  ...args: TArgs
): () => void {
  let fn: (e: T) => void;
  let layerId: string | undefined;

  if (typeof layerIdOrHandler === 'string' && maybeHandler) {
    // layer-specific event
    layerId = layerIdOrHandler;
    fn = (e) => maybeHandler(e, ...args);
    map.on(type, layerId, fn);
  } else if (typeof layerIdOrHandler === 'function') {
    // map-wide event
    fn = (e) => layerIdOrHandler(e, ...args);
    map.on(type, fn);
  } else {
    throw new Error('Invalid arguments passed to registerMapEvent');
  }

  // Cleanup always removes the exact same callback reference
  return () => {
    if (layerId) map.off(type, layerId, fn);
    else map.off(type, fn);
  };
}
